/**
 * Photo originals in the company's own storage (MB's decision 643, built under decision 838;
 * docs/specs/cloud/project-photos.md §14). A photo's original goes to the tenant's Nextcloud through the archive;
 * once it is there and reads back with the same SHA-256, Teslyn Cloud removes its own original and keeps the display
 * size and thumbnail; "Download original" fetches it from the Nextcloud through Teslyn Cloud. This file has the
 * decided numbers and the words; it has no imports, and an identical copy is
 * `supabase/functions/_shared/photo-originals.ts` (tools/dev/copy-edge-shared.mjs; a test fails if they differ).
 */

/** Photos moved per background run (pg_cron every 15 minutes), and the bytes read per run at most. */
export const PHOTO_ORIGINALS_PER_RUN = 20;
export const PHOTO_ORIGINALS_BYTES_PER_RUN = 300 * 1024 * 1024;

/**
 * How long after its original reached Teslyn Cloud a photo waits before Teslyn Cloud copies it itself: the laptop
 * that added it archives it in the same sync, so the move normally finds that copy and only reads it back.
 */
export const PHOTO_ORIGINAL_GRACE_MINUTES = 60;

/** Where a photo's original is, as photo_original_source answers. */
export type PhotoOriginalSource =
  | {
      readonly status: 'cloud';
      readonly object_path: string;
      readonly file_name: string;
      readonly file_ext: string;
    }
  | {
      readonly status: 'nextcloud';
      readonly path: string;
      readonly file_name: string;
      readonly file_ext: string;
    }
  | { readonly status: 'not_uploaded' }
  | { readonly status: 'not_found' };

const TYPES: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/** The content type a downloaded original is served with. */
export function photoOriginalContentType(ext: string): string {
  return TYPES[ext.toLowerCase()] ?? 'application/octet-stream';
}

/** `attachment; filename="DJI_0007.JPG"; filename*=UTF-8''DJI_0007.JPG`, with anything unsafe taken out. */
export function photoOriginalDisposition(fileName: string): string {
  const base = (fileName.split(/[\\/]/).pop() ?? '').trim() || 'photo';
  const ascii = base.replace(/[^\x20-\x7e]/g, '_').replace(/["\\]/g, '_');
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(base)}`;
}

/** What the photo viewer says under "Download original" for staff. */
export function photoOriginalPlaceText(where: 'cloud' | 'nextcloud' | 'not_uploaded'): string {
  switch (where) {
    case 'nextcloud':
      return "Kept in your organisation's Nextcloud; Teslyn Cloud fetches it for you.";
    case 'not_uploaded':
      return 'The original is not in Teslyn Cloud yet.';
    case 'cloud':
    default:
      return '';
  }
}

/** Errors of "Download original" in words (Teslyn's own; never the Nextcloud's body, which may name paths). */
export const PHOTO_ORIGINAL_WORDS = {
  not_found: 'This photo is not one you can download.',
  not_uploaded:
    'The original is not in Teslyn Cloud yet. It can be downloaded once the computer that added it has synced.',
  not_connected:
    "The original is in your organisation's Nextcloud, which is no longer connected to Teslyn Cloud. A tenant admin connects it again in Integrations, File archive.",
  missing:
    "The original is not in your organisation's Nextcloud any more (it may have been moved or deleted there). Look for it in the project's day folder in Nextcloud.",
  unreachable: "Teslyn Cloud could not reach your organisation's Nextcloud. Try again later.",
  mismatch:
    "The copy in your organisation's Nextcloud did not read back the same as the photo. Teslyn Cloud keeps its own original and tries again later.",
  cloud_mismatch:
    "Teslyn Cloud's own original is not the photo that was added (its SHA-256 differs), so it is not moved. Tell Braynewave.",
} as const;

/**
 * What Teslyn Field says when it opens the display size because the original is in the Nextcloud (decision 643):
 * Field cannot open a Teslyn Cloud download in the computer's viewer with the person's sign-in.
 */
export const PHOTO_ORIGINAL_FIELD_NOTE =
  "Opened the display size. The original is kept in your organisation's Nextcloud: download it in Teslyn Cloud, Photos, or open it in Nextcloud.";
